import { progressDataManager } from "@core/service-container";
// ProgramId import removed - now using string type instead
import { ComplianceGraphData, FeedbackGraphData, ProgressData } from "@domain/progress/progress-data";
import { useComputedObservable } from "micro-observables";
import { useEffect, useState } from "react";

export function useProgressData(
	patientId: string,
	programId: string | undefined,
	withFetch = true,
): { loading: boolean; progressData: ProgressData | undefined } {
	const [loading, setLoading] = useState(withFetch && !!programId);

	const progressData = useComputedObservable<ProgressData | undefined>(
		() => (programId ? progressDataManager.progressDataByPatient.get().get(patientId)?.get(programId) : undefined),
		[patientId, programId],
	);

	useEffect(() => {
		const getProgressData = async (patientIdToFetch: string, programIdToFetch: string) => {
			try {
				setLoading(true);
				await progressDataManager.fetchProgressData(patientIdToFetch, programIdToFetch);
			} catch (e) {
				console.error(e);
			} finally {
				setLoading(false);
			}
		};
		withFetch && programId && getProgressData(patientId, programId);
	}, [patientId, programId, withFetch]);

	return { loading, progressData };
}

export function useComplianceGraphData(
	patientId: string,
	programId: string | undefined,
	withFetch = true,
): { loading: boolean; complianceGraphData: ComplianceGraphData | undefined } {
	const [loading, setLoading] = useState(withFetch && !!programId);

	const complianceGraphData = useComputedObservable<ComplianceGraphData | undefined>(
		() =>
			programId ? progressDataManager.complianceGraphDataByPatient.get().get(patientId)?.get(programId) : undefined,
		[patientId, programId],
	);

	useEffect(() => {
		const getComplianceGraphData = async (patientIdToFetch: string, programIdToFetch: string) => {
			try {
				setLoading(true);
				await progressDataManager.fetchComplianceGraphData(patientIdToFetch, programIdToFetch);
			} catch (e) {
				console.error(e);
			} finally {
				setLoading(false);
			}
		};
		withFetch && programId && getComplianceGraphData(patientId, programId);
	}, [patientId, programId, withFetch]);

	return { loading, complianceGraphData };
}

export function useFeedbackGraphData(
	patientId: string,
	programId: string | undefined,
	withFetch = true,
): { loading: boolean; feedbackGraphData: FeedbackGraphData | undefined } {
	const [loading, setLoading] = useState(withFetch && !!programId);

	const feedbackGraphData = useComputedObservable<FeedbackGraphData | undefined>(
		() => (programId ? progressDataManager.feedbackGraphDataByPatient.get().get(patientId)?.get(programId) : undefined),
		[patientId, programId],
	);

	useEffect(() => {
		const getFeedbackGraphData = async (patientIdToFetch: string, programIdToFetch: string) => {
			try {
				setLoading(true);
				await progressDataManager.fetchFeedbackGraphData(patientIdToFetch, programIdToFetch);
			} catch (e) {
				console.error(e);
			} finally {
				setLoading(false);
			}
		};
		withFetch && programId && getFeedbackGraphData(patientId, programId);
	}, [patientId, programId, withFetch]);

	return { loading, feedbackGraphData };
}
